import React, { useMemo } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useTranslatedMessages } from '../../../../hooks/useTranslatedMessages';

const STATUS_COLORS = {
  APPROVED: 'success.main',
  PENDING: 'warning.main',
  REJECTED: 'error.main',
  VALIDATION_FAILED: 'warning.dark',
};

const FALLBACK_COLORS = ['#22c55e', '#f59e0b', '#ef4444', '#a16207'];

const ComplianceSummaryDonut = ({ byStatus = [], loading = false }) => {
  const theme = useTheme();
  const { misDashboard: m } = useTranslatedMessages();

  const chartData = useMemo(
    () =>
      (byStatus ?? []).map((item, index) => {
        const paletteKey = STATUS_COLORS[item.status];
        const color = paletteKey
          ? paletteKey.split('.').reduce((acc, key) => acc?.[key], theme.palette) ||
            FALLBACK_COLORS[index % FALLBACK_COLORS.length]
          : FALLBACK_COLORS[index % FALLBACK_COLORS.length];
        return {
          name: item.label || item.status,
          value: item.count ?? 0,
          color,
        };
      }),
    [byStatus, theme.palette],
  );

  if (loading) {
    return <Skeleton variant="rounded" height={260} />;
  }

  if (!chartData.length) {
    return (
      <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {m.compliance?.no_data ?? 'No compliance data available for the selected filters.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
        {m.compliance?.summary_title ?? 'Compliance Summary'}
      </Typography>
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie data={chartData} cx="50%" cy="50%" innerRadius={54} outerRadius={86} paddingAngle={2} dataKey="value">
            {chartData.map((entry) => (
              <Cell key={entry.name} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 8,
              color: theme.palette.text.primary,
            }}
          />
          <Legend
            verticalAlign="bottom"
            height={36}
            formatter={(value, entry) => (
              <span style={{ fontSize: 12, color: theme.palette.text.secondary }}>
                {value}: {entry?.payload?.value ?? 0}
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default ComplianceSummaryDonut;
